import {
  PROGRAM_QUERY_GC_TIME,
  PROGRAM_QUERY_STALE_TIMES,
  programQueryKeys,
} from "@/dashboard/programQueryConfig";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { useQueryClient } from "@tanstack/react-query";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useEffect, useRef, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import "./MapPrograms.css";

const getMapStyleUrl = () => {
  const stadiaApiKey = import.meta.env.VITE_API_KEY_STADIAMAP;

  if (!stadiaApiKey) {
    return "https://demotiles.maplibre.org/style.json";
  }

  return `https://tiles.stadiamaps.com/styles/alidade_smooth.json?api_key=${stadiaApiKey}`;
};

type ProgramMapFeature = {
  type: "Feature";
  geometry: {
    type: "Point";
    coordinates: [number, number];
  };
  properties: {
    title: string;
    coordinates: [number, number][];
    project: any;
    program: any;
    polygons: any[];
  };
};

const MapSkeleton = () => (
  <div className="pointer-events-none absolute inset-0 z-10">
    <Skeleton className="h-full w-full rounded-lg bg-[#ecedee]" />
  </div>
);

const MapClusterProgramsAll = ({ dataRead }) => {
  const queryClient = useQueryClient();
  const foldersQueryKey = [programQueryKeys.carrierFolders];
  const folders = useFetchDataV2<any[]>(
    `${import.meta.env.VITE_API_ENDPOINT}/folders/all?context=carrier`,
    {
      browserCache: true,
      browserCacheKey: "carrier-folders",
      browserCacheNamespace: "program-map",
      cacheId: foldersQueryKey,
      gcTime: PROGRAM_QUERY_GC_TIME,
      staleTime: PROGRAM_QUERY_STALE_TIMES.folders,
    },
  );
  const cachedFolders = queryClient.getQueryData<any[]>(foldersQueryKey);
  const foldersData = Array.isArray(folders.data)
    ? folders.data
    : Array.isArray(cachedFolders)
      ? cachedFolders
      : [];

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null);
  const hasZoomedRef = useRef(false);
  const markersRef = useRef<maplibregl.Marker[]>([]);

  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current) {
      const map = new maplibregl.Map({
        container: mapContainerRef.current,
        style: getMapStyleUrl(),
        center: [0, 0],
        zoom: 2,
      });
      mapRef.current = map;

      map.addControl(new maplibregl.NavigationControl());

      map.on("load", () => {
        try {
          map.setPaintProperty("water", "fill-color", "#75CFF0");
          map.setPaintProperty("background", "background-color", "#E7EBCC");
          map.setPaintProperty("building", "fill-color", "#E7EBCC");
        } catch {}
        map.resize();
      });

      const resizeIfCurrent = () => {
        if (mapRef.current === map) {
          map.resize();
        }
      };
      window.requestAnimationFrame(resizeIfCurrent);
      window.setTimeout(resizeIfCurrent, 250);
      setMapInstance(map);
    }
    // 🔴 pas de setMapInstance(null) ici : on ne veut pas reset la map à chaque rerender
  }, []);

  useEffect(() => {
    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapInstance) return;

    const resizeMap = () => mapInstance.resize();
    const frameId = window.requestAnimationFrame(resizeMap);
    const timerId = window.setTimeout(resizeMap, 250);
    const resizeObserver =
      typeof ResizeObserver !== "undefined" && mapContainerRef.current
        ? new ResizeObserver(resizeMap)
        : null;

    resizeObserver?.observe(mapContainerRef.current as HTMLDivElement);
    window.addEventListener("resize", resizeMap);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timerId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", resizeMap);
    };
  }, [mapInstance, dataRead]);
  useEffect(() => {
    if (!mapInstance || foldersData.length === 0) return;

    // ✅ NEW: set d'IDs sélectionnés (si dataRead est un tableau d'ids)
    const selectedIds =
      Array.isArray(dataRead) && dataRead.length > 0
        ? new Set<number | string>(dataRead as (number | string)[])
        : null;

    const markerCollection: {
      type: "FeatureCollection";
      features: ProgramMapFeature[];
    } = {
      type: "FeatureCollection",
      features: [],
    };

    // Affiche les marqueurs
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    foldersData.forEach((row) => {
      // ✅ NEW: si on a une sélection et que l'id projet ne matche pas, on skip
      if (selectedIds && !selectedIds.has(row?.project?.id)) {
        return;
      }

      // ⚠️ On reconstitue les variables attendues par l'ancien code
      const program = row.program || { name: "-" };
      const project = row.project || { name: "-" };

      const allCoordinates: [number, number][] = [];

      // Nouveau format : row.locations[].coordinates déjà en [lng, lat] (on ne touche pas ta logique existante)
      if (row.locations) {
        row.locations.forEach((loc) => {
          (loc.coordinates || []).forEach((coord) => {
            // Validation minimale + push direct
            if (Array.isArray(coord) && coord.length === 2) {
              allCoordinates.push([coord[1], coord[0]]); // <-- inchangé
            }
          });
        });
      }

      // Nouveau format : row.parcels (si présent) -> même logique, on ignore les styles
      if (row.parcels) {
        row.parcels.forEach((par) => {
          (par.coordinates || []).forEach((coord) => {
            if (Array.isArray(coord) && coord.length === 2) {
              allCoordinates.push([coord[1], coord[0]]); // <-- inchangé
            }
          });
        });
      }

      if (allCoordinates.length > 0) {
        const clusters = new Map<string, [number, number][]>();
        allCoordinates.forEach((coord) => {
          const key = `${Math.round(coord[0])}:${Math.round(coord[1])}`;
          const cluster = clusters.get(key) ?? [];
          cluster.push(coord);
          clusters.set(key, cluster);
        });

        clusters.forEach((cluster) => {
          const clusterCenter = cluster.reduce(
            (center, coord) => [center[0] + coord[0], center[1] + coord[1]],
            [0, 0],
          );
          const center: [number, number] = [
            clusterCenter[0] / cluster.length,
            clusterCenter[1] / cluster.length,
          ];
          markerCollection.features.push({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: center,
            },
            properties: {
              title: `programme : ${program.name} <br>project: ${project.name}`,
              coordinates: cluster,
              project,
              program,
              polygons: Array.isArray(row.locations)
                ? row.locations
                    .map((loc) =>
                      Array.isArray(loc.coordinates) ? loc.coordinates : null
                    )
                    .filter(Boolean)
                : [],
            },
          });
        });
      }
    });

    markerCollection.features.forEach((feature) => {
      const el = document.createElement("div");
      el.className = "marker";
      el.style.width = "25px";
      el.style.height = "25px";
      el.style.borderRadius = "50%";

      const marker = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat(feature.geometry.coordinates)
        .setPopup(
          new maplibregl.Popup({ offset: 25 }).setHTML(
            `<div>${feature.properties.title}</div>`
          )
        )
        .addTo(mapInstance);

      marker.getElement().addEventListener("click", () => {
        displayClusterPolygons(feature, mapInstance);
      });
      markersRef.current.push(marker);
    });

    // Fit bounds une fois
    const bounds = new maplibregl.LngLatBounds();
    markerCollection.features.forEach((feature) => {
      bounds.extend(feature.geometry.coordinates);
    });

    if (!hasZoomedRef.current && !bounds.isEmpty()) {
      mapInstance.fitBounds(bounds, {
        padding: 50,
        duration: 1000,
      });
      hasZoomedRef.current = true;
    }

    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      hasZoomedRef.current = false;
    };
  }, [mapInstance, foldersData, dataRead]);

  const displayClusterPolygons = (feature, map) => {
    // Supprimer l'ancien polygone s'il existe
    if (map.getSource("polygon-source")) {
      map.removeLayer("polygon-layer");
      map.removeSource("polygon-source");
    }
    const polygons = feature?.properties?.polygons;
    console.log("feature", feature);
    if (!Array.isArray(polygons) || polygons.length === 0) {
      console.error(
        "Aucune donnée de polygone trouvée dans feature.properties.polygons !"
      );
      return;
    }

    // Construire les polygones avec des boucles fermées
    const geojsonFeatures = polygons
      .map((coords) => {
        if (!Array.isArray(coords) || coords.length === 0) return null; // Vérification des données

        const polygonCoords = coords.map((coord) => {
          if (coord.length === 2) {
            return [coord[1], coord[0]]; // Inversion lat/lng -> [lng, lat]
          }
          return coord;
        });
        if (
          polygonCoords[0].toString() !==
          polygonCoords[polygonCoords.length - 1].toString()
        ) {
          polygonCoords.push([...polygonCoords[0]]); // Fermer le polygone
        }

        return {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: [polygonCoords], // Chaque ensemble de coordonnées forme un polygone fermé
          },
        };
      })
      .filter(Boolean); // Filtrer les valeurs nulles

    if (geojsonFeatures.length === 0) {
      console.error("Aucune donnée valide pour le polygone !");
      return;
    }

    // Ajouter la source GeoJSON
    map.addSource("polygon-source", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: geojsonFeatures,
      },
    });

    // Ajouter un calque pour afficher les polygones
    map.addLayer({
      id: "polygon-layer",
      type: "fill",
      source: "polygon-source",
      paint: {
        "fill-color": "rgba(0, 0, 255, 0.5)", // Couleur bleue avec transparence
        "fill-outline-color": "rgba(0, 0, 255, 0.5)", // Bordure bleue
      },
    });

    // Construire le polygone en fermant la boucle (le dernier point doit être le même que le premier)
    const polygonCoordinates = [...feature.properties.coordinates];
    if (
      polygonCoordinates[0].toString() !==
      polygonCoordinates[polygonCoordinates.length - 1].toString()
    ) {
      polygonCoordinates.push(polygonCoordinates[0]);
    }
    // Définir les limites pour bien cadrer le polygone
    const bounds = new maplibregl.LngLatBounds();
    polygonCoordinates.forEach((coord) => bounds.extend(coord));

    // Vérifier que les limites ne sont pas vides avant d'ajuster la vue
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, {
        padding: 50,
        duration: 1000, // Animation douce
      });
    }
  };

  const displayClusterPolygon = (feature, map) => {
    // Vérifier si des données valides existent
    if (
      !feature.properties.coordinates ||
      feature.properties.coordinates.length === 0
    ) {
      console.error("Aucune coordonnée trouvée pour le polygone !");
      return;
    }

    // Supprimer l'ancien polygone s'il existe
    if (map.getSource("polygon-source")) {
      map.removeLayer("polygon-layer");
      map.removeSource("polygon-source");
    }

    // Construire le polygone en fermant la boucle (le dernier point doit être le même que le premier)
    const polygonCoordinates = [...feature.properties.coordinates];
    if (
      polygonCoordinates[0].toString() !==
      polygonCoordinates[polygonCoordinates.length - 1].toString()
    ) {
      polygonCoordinates.push(polygonCoordinates[0]);
    }

    // Ajouter la source GeoJSON du polygone
    map.addSource("polygon-source", {
      type: "geojson",
      data: {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [polygonCoordinates], // Forme correcte pour un polygone
        },
      },
    });

    // Ajouter un calque pour afficher le polygone
    map.addLayer({
      id: "polygon-layer",
      type: "fill",
      source: "polygon-source",
      paint: {
        "fill-color": "rgba(0, 0, 255, 0.5)", // Bleu semi-transparent
        "fill-outline-color": "blue", // Bordure bleue
      },
    });

    // Définir les limites pour bien cadrer le polygone
    const bounds = new maplibregl.LngLatBounds();
    polygonCoordinates.forEach((coord) => bounds.extend(coord));

    // Vérifier que les limites ne sont pas vides avant d'ajuster la vue
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, {
        padding: 50,
        duration: 1000, // Animation douce
      });
    }
  };
  return (
    <div className="relative min-h-[500px] w-full">
      {!mapInstance && <MapSkeleton />}
      <div
        ref={mapContainerRef}
        style={{ minHeight: "500px", height: "75vh", width: "100%", zIndex: "1" }}
      />
    </div>
  );
};

export default MapClusterProgramsAll;
